import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { getProgressState, recordTranslation as recordTranslationInStore, type Celebration, type ProgressState } from '@/lib/progress/store'
import { fetchPercentileThresholds, pullProgressFromCloud, syncProgressToCloud } from '@/lib/progress/sync'

function celebrationToast(celebration: Celebration): void {
  if (celebration.kind === 'badge') {
    toast.success(`Badge Unlocked: ${celebration.badge.name}`, {
      description: celebration.badge.toastSubtitle,
    })
    return
  }
  if (celebration.kind === 'mastery') {
    toast.success(`Level Up: ${celebration.level.name}`, {
      description: `Hebrew Mastery Level ${celebration.level.level}`,
    })
    return
  }
  const label = celebration.tier === 'top1' ? 'Top 1%' : celebration.tier === 'top5' ? 'Top 5%' : 'Top 10%'
  toast.success(`${label} Baba User`, {
    description: 'You are now among the most active translators on baba.',
  })
}

/**
 * The server pull and the percentile table, once per page load for every
 * consumer. It used to run per component instance, so the translator, the
 * rail's plan card (re-mounted on every expand) and the account menu each
 * fetched /me and the percentiles again.
 */
let cloudPull: Promise<void> | null = null
let pendingCelebration: Celebration | null = null

function pullProgressOnce(): Promise<void> {
  if (!cloudPull) {
    // Pull server totals first so the percentile check (and the numbers on
    // screen) reflect the account's real totals, not just what this browser
    // has translated.
    cloudPull = pullProgressFromCloud()
      .then(() => fetchPercentileThresholds())
      .then((celebration) => {
        pendingCelebration = celebration
      })
  }
  return cloudPull
}

/**
 * Reactive wrapper around lib/progress/store.ts: streak, badges, mastery,
 * and percentile standing, all localStorage-backed. `recordTranslation`
 * updates local state synchronously, toasts any newly-unlocked celebration,
 * and syncs to the cloud in the background (best-effort — see
 * lib/progress/sync.ts).
 */
export function useProgress() {
  const [state, setState] = useState<ProgressState>(() => getProgressState())

  useEffect(() => {
    let cancelled = false
    void pullProgressOnce().then(() => {
      if (cancelled) return
      setState(getProgressState())
      // Whichever consumer gets here first shows it; the rest find it taken.
      const celebration = pendingCelebration
      pendingCelebration = null
      if (celebration) celebrationToast(celebration)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const recordTranslation = useCallback((input: { chars: number }) => {
    const { state: nextState, celebrations } = recordTranslationInStore(input)
    setState(nextState)
    for (const celebration of celebrations) celebrationToast(celebration)
    void syncProgressToCloud()
    return { state: nextState, celebrations }
  }, [])

  const refresh = useCallback(() => {
    setState(getProgressState())
  }, [])

  return { ...state, recordTranslation, refresh }
}
